import React from 'react';
import { prefixClass } from 'lib/utils';
import classNames from 'classnames';

import './TransactionsTableTableCell.scss';
import TransactionsTableAttachmentsCell from '../TransactionsTableAttachmentsCell';
import { Transaction } from '../../TransactionsTable';
import { ReportTransactionDataRow } from '@hone-automation/common';
import { IconFlag, IconFlagFilled } from 'components/Icons';
import { Tooltip, Whisper } from 'rsuite';
import { mappedTypesTitle } from 'presentation/components/RightPanelModules/Chart/context/TransactionsTableContext';
import { getFlaggingTooltipMessage } from '../../utils';

export type TransactionType = 'bill' | 'je' | 'vc' | 'dep';

type TransactionsTableTableCellProps = {
  value: React.ReactNode;
  cellType?: 'type' | 'number' | 'string' | 'dueDate' | 'date' | 'links' | 'flag' | 'reference';
  typeData?: TransactionType;
  align?: 'left' | 'center' | 'right';
  color?: 'normal' | 'highlight' | 'shaded';
  width?: string;
  transaction?: Transaction | undefined;
  attachmentsLoading?: boolean;
  attachmentsData?: ReportTransactionDataRow[] | undefined;
  onFlagClick?: () => void;
  allowFlagging?: boolean;
  isConsolidated?: boolean;
  isBeginningBalance?: boolean;
};

type TableItemTypeProps = {
  typeData?: TransactionType;
  align?: 'left' | 'center' | 'right';
};

const prefix = prefixClass('transactions-table-cell-data');

const TableItemType = ({ typeData, align }: TableItemTypeProps) => {
  const typeClass = classNames(prefix('type-item'), {
    [`text-${align || 'left'}`]: align,
    [`${typeData?.toLocaleLowerCase().replaceAll('.', '')}`]: typeData,
  });
  return (
    <Whisper
      placement="top"
      controlId="control-type-item"
      trigger="hover"
      speaker={<Tooltip>{mappedTypesTitle[typeData as keyof typeof mappedTypesTitle]}</Tooltip>}
    >
      <span className={typeClass}>{typeData}</span>
    </Whisper>
  );
};

const TransactionsTableTableCell: React.FC<TransactionsTableTableCellProps> = ({
  align,
  value,
  cellType,
  typeData,
  color = 'normal',
  width,
  transaction,
  attachmentsLoading,
  attachmentsData,
  onFlagClick,
  allowFlagging = true,
  isConsolidated = false,
  isBeginningBalance = false,
}) => {
  const isFlaggingDisabled = !allowFlagging;
  if (cellType === 'links' && transaction) {
    return (
      <>
        <TransactionsTableAttachmentsCell
          width={width}
          transaction={transaction}
          attachmentsLoading={attachmentsLoading}
          attachmentsData={attachmentsData}
        />
      </>
    );
  }
  if (cellType === 'type' && typeData) {
    return (
      <td width={width} className={prefix(`${cellType}`)}>
        <TableItemType typeData={typeData} />
      </td>
    );
  }

  if (cellType === 'reference') {
    return (
      <Whisper trigger="hover" delay={1200} delayClose={100} placement="bottom" speaker={<Tooltip>{value}</Tooltip>}>
        <td width={width} className={prefix(`${cellType}`)}>
          {value}
        </td>
      </Whisper>
    );
  }

  if (cellType === 'flag') {
    const content = (
      <td
        width={width}
        className={classNames(prefix(`${cellType}`), {
          'not-hovered': value === 'not-hovered',
          disabled: isFlaggingDisabled && value === 'hovered',
        })}
        onClick={() => !isFlaggingDisabled && onFlagClick?.()}
      >
        {value === 'flagged' ? <IconFlagFilled /> : <IconFlag />}
      </td>
    );

    if (isFlaggingDisabled) {
      return (
        <Whisper
          placement="top"
          speaker={<Tooltip>{getFlaggingTooltipMessage(allowFlagging, isConsolidated, isBeginningBalance)}</Tooltip>}
        >
          {content}
        </Whisper>
      );
    }

    return content;
  }

  if (cellType === 'date' || cellType === 'string') {
    return (
      <td width={width} className={classNames(prefix(), `color-${color}`)}>
        {value}
      </td>
    );
  }

  return (
    <td
      width={width}
      className={classNames(prefix(), `text-${align}`, `color-${color}`)}
      onClick={() => onFlagClick?.()}
    >
      {value}
    </td>
  );
};

export default TransactionsTableTableCell;
